import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { RotateCcw, Trophy } from "lucide-react";
import { MEMORY_PAIRS } from "../../lib/constants";
import { submitScore } from "../../lib/api";

const shuffle = (arr) => {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
};

const buildDeck = () =>
  shuffle(
    MEMORY_PAIRS.flatMap((p, idx) => [
      { uid: `${p.id}-a-${idx}`, pairId: p.id, label: p.label, mark: p.emoji },
      { uid: `${p.id}-b-${idx}`, pairId: p.id, label: p.label, mark: p.emoji },
    ])
  );

export default function MemoryMatch() {
  const [deck, setDeck] = useState(buildDeck);
  const [flipped, setFlipped] = useState([]);
  const [matched, setMatched] = useState(new Set());
  const [moves, setMoves] = useState(0);
  const [submitted, setSubmitted] = useState(false);

  const allMatched = matched.size === deck.length;

  useEffect(() => {
    if (flipped.length !== 2) return;
    const [a, b] = flipped;
    setMoves((m) => m + 1);
    if (deck[a].pairId === deck[b].pairId) {
      setMatched((prev) => {
        const next = new Set(prev);
        next.add(a);
        next.add(b);
        return next;
      });
      const t = setTimeout(() => setFlipped([]), 500);
      return () => clearTimeout(t);
    } else {
      const t = setTimeout(() => setFlipped([]), 900);
      return () => clearTimeout(t);
    }
  }, [flipped, deck]);

  useEffect(() => {
    if (allMatched && !submitted) {
      const points = Math.max(10, 200 - moves * 8);
      submitScore("memory", "guest", points).catch(() => {});
      setSubmitted(true);
    }
  }, [allMatched, submitted, moves]);

  const flip = (i) => {
    if (flipped.length === 2) return;
    if (flipped.includes(i)) return;
    if (matched.has(i)) return;
    setFlipped((f) => [...f, i]);
  };

  const reset = () => {
    setDeck(buildDeck());
    setFlipped([]);
    setMatched(new Set());
    setMoves(0);
    setSubmitted(false);
  };

  const stones = useMemo(() => Math.max(10, 200 - moves * 8), [moves]);

  return (
    <div data-testid="memory-game" className="w-full">
      <div className="flex items-center justify-between mb-4">
        <span className="text-xs uppercase tracking-[0.22em] font-bold text-[#8B0000]">
          Moves: <span className="text-[#1A1414]">{moves}</span>
        </span>
        <span className="text-xs uppercase tracking-[0.22em] font-bold text-[#8B0000]">
          Hero Stones: <span className="text-[#1A1414]">{stones}</span>
        </span>
      </div>

      <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
        {deck.map((card, i) => {
          const isOpen = flipped.includes(i) || matched.has(i);
          return (
            <motion.button
              key={card.uid}
              data-testid={`memory-card-${i}`}
              onClick={() => flip(i)}
              whileHover={{ scale: isOpen ? 1 : 1.03 }}
              whileTap={{ scale: 0.97 }}
              className="relative aspect-[3/4] rounded-2xl perspective select-none"
              aria-label={isOpen ? card.label : "Hidden card"}
            >
              <motion.div
                animate={{ rotateY: isOpen ? 180 : 0 }}
                transition={{ duration: 0.4 }}
                className="relative w-full h-full transform-style-3d"
                style={{ transformStyle: "preserve-3d" }}
              >
                {/* Back */}
                <div
                  className="absolute inset-0 rounded-2xl bg-gradient-to-br from-[#E60000] to-[#8B0000] flex items-center justify-center shadow-arc"
                  style={{ backfaceVisibility: "hidden" }}
                >
                  <span className="font-display font-black text-3xl text-white/30">?</span>
                </div>
                {/* Front */}
                <div
                  className={`absolute inset-0 rounded-2xl flex flex-col items-center justify-center p-2 text-center border-2 ${
                    matched.has(i)
                      ? "bg-emerald-50 border-emerald-400"
                      : "bg-white border-[#FF8080]/40"
                  }`}
                  style={{
                    backfaceVisibility: "hidden",
                    transform: "rotateY(180deg)",
                  }}
                >
                  <span className="font-display font-black text-3xl text-[#8B0000] mb-1">
                    {card.mark}
                  </span>
                  <span className="text-[10px] uppercase tracking-wider font-bold text-[#1A1414]/70">
                    {card.label}
                  </span>
                </div>
              </motion.div>
            </motion.button>
          );
        })}
      </div>

      {allMatched && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="mt-6 p-5 rounded-2xl bg-[#1A1414] text-white text-center"
          data-testid="memory-results"
        >
          <Trophy size={32} className="mx-auto text-[#FF8080] mb-1" />
          <p className="font-display font-black text-xl">Pairs locked.</p>
          <p className="text-sm text-white/70 mt-1">
            You earned <span className="text-[#FF8080] font-bold">{stones}</span> Hero
            Stones in {moves} moves.
          </p>
          <button
            onClick={reset}
            data-testid="memory-replay-btn"
            className="mt-4 inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#E60000] text-white font-display font-bold shadow-arc hover:bg-[#FF8080] hover:text-[#8B0000] transition-all"
          >
            <RotateCcw size={16} /> Reshuffle
          </button>
        </motion.div>
      )}
    </div>
  );
}
